// Global new-message popups for affiliate chat. Polls
// /api/affiliate-messaging/new-messages every 12s and shows:
//  - in-app toast popups (top-right, click to open the chat) on any page
//    except the messages page itself while it's visible, and
//  - native browser/desktop notifications when the tab is hidden
//    (asks for Notification permission once).
// Mounted globally for MARS users (src/index.js) and inside the affiliate
// portal for affiliates.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, X } from 'lucide-react';
import { onChatEvent } from './chatSocket';

const getApiUrl = () => process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

// Real-time pushes over the socket do the heavy lifting (instant, immune to
// background-tab timer throttling); this poll is only a slow fallback.
const POLL_MS = 30000;
const TOAST_MS = 6000;

const ChatNotifications = ({ role }) => {
  const navigate = useNavigate();
  const [toasts, setToasts] = useState([]);
  const lastIdRef = useRef(null); // null until initialized from the backend
  const messagesPath = role === 'affiliate' ? '/affiliate/messages' : '/messages';

  // Ask for desktop-notification permission once (browser remembers).
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      try { Notification.requestPermission().catch(() => {}); } catch (_) { /* older API */ }
    }
  }, []);

  const dismissToast = (id) => setToasts(prev => prev.filter(t => t.id !== id));

  // Soft navigation — no full page reload, so the sidebar/app chrome stays put.
  const openConversation = useCallback((conversationId) => {
    navigate(`${messagesPath}?open=${conversationId}`);
  }, [messagesPath, navigate]);

  const poll = useCallback(async () => {
    try {
      const after = lastIdRef.current === null ? -1 : lastIdRef.current;
      const response = await fetch(
        `${getApiUrl()}/api/affiliate-messaging/new-messages?afterId=${after}`,
        { credentials: 'include', headers: { 'X-Mars-Actor': role } }
      );
      if (!response.ok) return; // logged out / session gone — stay quiet
      const data = await response.json();
      if (!data.success) return;

      if (lastIdRef.current === null) {
        lastIdRef.current = data.latestId; // initialize, no popups for history
        return;
      }
      if (!data.messages.length) return;
      lastIdRef.current = Math.max(lastIdRef.current, data.latestId);

      const tabVisible = document.visibilityState === 'visible';

      // In-app toasts — shown on every page (including the chat pages, so
      // affiliates always see incoming texts; already-read messages are
      // filtered out server-side, which keeps the open thread quiet).
      setToasts(prev => [
        ...prev,
        ...data.messages.map(m => ({
          id: m.id,
          conversationId: m.conversation_id,
          sender: `${m.sender_first_name || ''} ${m.sender_last_name || ''}`.trim() || 'New message',
          preview: m.message_type === 'image' ? '📷 Photo' : (m.body || '').slice(0, 80)
        }))
      ].slice(-4)); // keep at most 4 stacked
      data.messages.forEach(m => {
        setTimeout(() => dismissToast(m.id), TOAST_MS);
      });

      // Desktop notifications — only when the tab is hidden.
      if (!tabVisible && 'Notification' in window && Notification.permission === 'granted') {
        data.messages.forEach(m => {
          try {
            const sender = `${m.sender_first_name || ''} ${m.sender_last_name || ''}`.trim() || 'Someone';
            const preview = m.message_type === 'image' ? '📷 Photo' : (m.body || '').slice(0, 120);
            const notification = new Notification('MARS - Chat Notification', {
              body: `${sender}: ${preview}`,
              icon: `${window.location.origin}/MARS-Color.png`,
              badge: `${window.location.origin}/MARS-Color.png`,
              tag: `mars-chat-${m.conversation_id}` // collapse per conversation
            });
            notification.onclick = () => {
              window.focus();
              openConversation(m.conversation_id);
              notification.close();
            };
          } catch (_) { /* notification construction can fail on some platforms */ }
        });
      }
    } catch (_) { /* retried on next poll */ }
  }, [messagesPath, openConversation]);

  useEffect(() => {
    poll();
    const interval = setInterval(poll, POLL_MS); // keeps running while hidden — that's the point
    return () => clearInterval(interval);
  }, [poll]);

  // Instant notifications pushed over the socket — fire the moment a message
  // is sent, even while the browser is minimized (no timer throttling).
  useEffect(() => {
    const unsubscribe = onChatEvent(role, (event) => {
      if (event.type !== 'message:new') return;
      const m = event.message;
      if (m.sender_role === role) return; // own message
      lastIdRef.current = Math.max(lastIdRef.current ?? 0, m.id); // poll won't re-show it

      const sender = event.senderName || 'New message';
      const preview = m.message_type === 'image' ? '📷 Photo' : (m.body || '').slice(0, 80);

      setToasts(prev => [
        ...prev,
        { id: m.id, conversationId: event.conversationId, sender, preview }
      ].slice(-4));
      setTimeout(() => dismissToast(m.id), TOAST_MS);

      if (document.visibilityState === 'hidden' && 'Notification' in window && Notification.permission === 'granted') {
        try {
          const notification = new Notification('MARS - Chat Notification', {
            body: `${sender}: ${preview}`,
            icon: `${window.location.origin}/MARS-Color.png`,
            badge: `${window.location.origin}/MARS-Color.png`,
            tag: `mars-chat-${event.conversationId}`
          });
          notification.onclick = () => {
            window.focus();
            openConversation(event.conversationId);
            notification.close();
          };
        } catch (_) { /* notification construction can fail on some platforms */ }
      }
    });
    return unsubscribe;
  }, [role, openConversation]);

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-[100] space-y-2 w-80">
      {toasts.map((t) => (
        <div
          key={t.id}
          className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl shadow-lg p-3 flex items-start gap-3 cursor-pointer hover:shadow-xl transition-shadow animate-[slideIn_.2s_ease-out]"
          onClick={() => openConversation(t.conversationId)}
        >
          <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center flex-shrink-0">
            <MessageSquare className="w-4 h-4 text-blue-600" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{t.sender}</p>
            <p className="text-xs text-gray-500 dark:text-slate-400 truncate">{t.preview || 'New message'}</p>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); dismissToast(t.id); }}
            className="text-gray-400 hover:text-gray-600 flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  );
};

export default ChatNotifications;
